import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { config } from 'config';
import { SWRConfig } from 'swr';

import { AppContextProvider } from 'hooks/context/useAppContext';
import { useAppInit } from 'hooks/init/useAppInit';

import { TApplicationContext } from 'types/app-context';

import { useHttpClient } from '../utils/httpClient/hooks/useHttpClient';
import { ConfiguredCoreFeaturesProvider } from './ConfiguredCoreFeaturesProvider';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(config.stripePubKey, { locale: 'en' });

type AppContextProviderProps = {
  context: TApplicationContext;
  children: JSX.Element;
};

export function AppProvider({ context, children }: AppContextProviderProps) {
  useAppInit(context);
  const { fetcher } = useHttpClient();

  return (
    <SWRConfig value={{ fetcher, shouldRetryOnError: false }}>
      <ReduxProvider store={context.store}>
        <AppContextProvider appContext={context}>
          <Elements
            stripe={stripePromise}
            options={{
              fonts: [
                {
                  src: 'url(https://fonts.gstatic.com/s/inter/v13/UcC73FwrK3iLTeHuS_fvQtMwCp50KnMa1ZL7W0Q5nw.woff2)',
                  family: 'Inter',
                  style: 'normal',
                },
              ],
            }}
          >
            <ConfiguredCoreFeaturesProvider
              coreFeaturesBackendData={context.coreFeaturesBackendData}
            >
              {children}
            </ConfiguredCoreFeaturesProvider>
          </Elements>
        </AppContextProvider>
      </ReduxProvider>
    </SWRConfig>
  );
}
